<template>
  <div class="rd-screen">
    <report-tools :copy="rows.length > 0" @copy="copyTable"></report-tools>

    <report-state v-if="loading" kind="loading"></report-state>
    <report-state
      v-else-if="!rows.length"
      icon="mdi-bowl-mix-outline"
      :title="emptyTitle"
      :text="emptyText"
    ></report-state>

    <ui-table
      v-else
      variant="report"
      :columns="columns"
      :rows="rows"
      :row-key="(row) => row.id || row.name"
      :sort="sort"
      :totals="totals"
      :total-label="$t('total')"
      @sort="sortBy"
    >
      <template #cell-source="{ row }">
        <span v-for="source in row.sources" :key="source" class="rd-status rd-status--muted rd-source">
          {{ sourceLabel(source) }}
        </span>
      </template>

      <template #cell-total="{ row }"><b>{{ row.total }}</b></template>

      <template #cell-share="{ row }">
        <span class="rd-share">
          <span class="rd-mini"><i :style="{ width: `${row.relative}%` }"></i></span>
          <span class="rd-muted rd-num">{{ row.share }}%</span>
        </span>
      </template>
    </ui-table>
  </div>
</template>

<script>
import ReportTabAllCompaniesSummary from "@/components/report/tabs/ReportTabAllCompaniesSummary.vue";
import { SHIFTS } from "@/constants/shifts";
import hasPlural from "../../shared/hasPlural";
import hasShiftBinding from "../../shared/hasShiftBinding";
import hasShiftSubtitle from "../../shared/hasShiftSubtitle";
import hasTableSort from "../hasTableSort";
import ReportState from "../ReportState.vue";
import ReportTools from "../ReportTools.vue";
import { copyText, rowsToTsv } from "../copyTable";

/**
 * "Sve firme – zbirni izveštaj" na računaru (Obroci ili Firme): stavka × smena sa udjelom.
 * Nasleđuje učitavanje, štampu i Excel iz ReportTabAllCompaniesSummary.
 */
export default {
  name: "ReportDesktopAllSummary",
  extends: ReportTabAllCompaniesSummary,
  mixins: [hasPlural, hasShiftBinding, hasShiftSubtitle, hasTableSort],
  components: { ReportState, ReportTools },
  props: {
    /** "meals" ili "companies" */
    mode: {
      type: String,
      default: "meals",
    },
  },
  data() {
    return { tab: this.mode === "companies" ? 1 : 0 };
  },
  computed: {
    /** Kolone tabele: iste služe za zaglavlje, redove i zbirni red. */
    columns() {
      return [
        { key: "#", label: "#", index: true },
        { key: "name", label: this.isCompaniesTab ? this.$t("firm") : this.$t("meal"), name: true, sortable: true },
        ...(this.isCompaniesTab ? [{ key: "source", label: this.$t("report_desktop.source") }] : []),
        ...this.visibleShifts.map((shift) => ({ key: shift.key, label: this.$t(shift.label), align: "end", num: true, zero: true, sortable: true })),
        { key: "total", label: this.$t("total"), align: "end", num: true, sortable: true },
        { key: "share", label: this.$t("report_desktop.share"), cls: "rd-col-share" },
      ];
    },
    /** Smene 1–3 uvek, četvrta samo kad ima obroka. */
    visibleShifts() {
      return this.selectedShiftColumns.filter(
        (shift) => shift.id < 4 || this.visibleRows.some((row) => row[shift.key] > 0)
      );
    },
    rows() {
      const searched = this.customSearch(this.visibleRows, [{ value: "name" }], this.filters.search);
      const grand = searched.reduce((sum, row) => sum + row.total, 0);
      const max = Math.max(1, ...searched.map((row) => row.total));
      return this.sortRows(
        searched.map((row) => ({
          ...row,
          share: grand ? Math.round((row.total / grand) * 100) : 0,
          relative: Math.round((row.total / max) * 100),
        }))
      );
    },
    totals() {
      const totals = { total: this.rows.reduce((sum, row) => sum + row.total, 0) };
      SHIFTS.forEach((shift) => {
        totals[shift.key] = this.rows.reduce((sum, row) => sum + (row[shift.key] || 0), 0);
      });
      return totals;
    },
    emptyTitle() {
      return this.filters.search
        ? this.$t("report_mobile.no_results_for", { search: this.filters.search })
        : this.$t("report_mobile.no_meals");
    },
    emptyText() {
      return this.filters.search ? "" : this.$t("report_desktop.empty_hint");
    },
    kpis() {
      return [
        { key: "total", label: this.$t("report_desktop.total_meals"), value: this.totals.total, tone: "inverse" },
        {
          key: "count",
          label: this.isCompaniesTab ? this.$t("companies") : this.$t("meals"),
          value: this.rows.length,
        },
      ];
    },
  },
  watch: {
    kpis: {
      handler(kpis) {
        this.$emit("kpis", this.rows.length ? kpis : []);
      },
      immediate: true,
    },
    loading(loading) {
      this.$emit("loading", loading);
    },
  },
  methods: {
    async copyTable() {
      const headers = [
        this.isCompaniesTab ? this.$t("firm") : this.$t("meal"),
        ...this.visibleShifts.map((shift) => this.$t(shift.label)),
        this.$t("total"),
      ];
      const rows = this.rows.map((row) => [
        row.name,
        ...this.visibleShifts.map((shift) => row[shift.key]),
        row.total,
      ]);
      const copied = await copyText(rowsToTsv(headers, rows));
      this.$emit("toast", this.$t(copied ? "report_desktop.copied" : "report_desktop.copy_failed"));
    },
  },
};
</script>
